// Laster referansedata (størrelser, typer, soner, prioriteter, ressurser, grupper,
// kategorier) én gang og eksponerer oppslagsfunksjoner for lesbare beskrivelser.
// Samme kilder som FilterPanel i rack-builder bruker.

import { useEffect, useMemo, useState } from 'react';
import { stcBinSizeService } from '../../../services/stcBinSizeService';
import { stcBinTypeService, StcBinTypeDto } from '../../../services/stcBinTypeService';
import { stcBinGrpService, StcBinGrpDto } from '../../../services/stcBinGrpService';
import { stcZoneService, StcZoneDto } from '../../../services/stcZoneService';
import { stcPriorityService, StcPriorityDto } from '../../../services/stcPriorityService';
import { stcResourceService, StcResourceDto } from '../../../services/stcResourceService';
import { StcBinSizeDto } from '../../../types/stcBinSize';
import { useStatusOptions } from '../statusPresentation';
import { BinDescriptionResolvers } from './types';

export interface BinReferenceData {
    isLoading: boolean;
    binSizes: StcBinSizeDto[];
    binTypes: StcBinTypeDto[];
    binGroups: StcBinGrpDto[];
    zones: StcZoneDto[];
    priorities: StcPriorityDto[];
    resources: StcResourceDto[];
    /** Lesbare beskrivelser: «{id} - {beskrivelse}», eller bare id hvis ukjent */
    describe: BinDescriptionResolvers;
    /** Kun beskrivelsen (uten id-prefiks) — for kompakt visning i celler */
    describeShort: BinDescriptionResolvers;
}

export function useBinReferenceData(companyNo: string): BinReferenceData {
    const [binSizes, setBinSizes] = useState<StcBinSizeDto[]>([]);
    const [binTypes, setBinTypes] = useState<StcBinTypeDto[]>([]);
    const [binGroups, setBinGroups] = useState<StcBinGrpDto[]>([]);
    const [zones, setZones] = useState<StcZoneDto[]>([]);
    const [priorities, setPriorities] = useState<StcPriorityDto[]>([]);
    const [resources, setResources] = useState<StcResourceDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Lokasjonskategorier (ST42) fra master-StatusCodes — språkbevisst
    const binCategoryOptions = useStatusOptions(42);

    useEffect(() => {
        if (!companyNo) return;
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            const results = await Promise.allSettled([
                stcBinSizeService.getByCompanyNo(companyNo),
                stcBinTypeService.getByCompanyNo(companyNo),
                stcBinGrpService.getByCompanyNo(companyNo),
                stcZoneService.getByCompanyNo(companyNo),
                stcPriorityService.getByCompanyNo(companyNo),
                stcResourceService.getByCompanyNo(companyNo),
            ]);
            if (cancelled) return;

            const [sizes, types, groups, zoneList, priorityList, resourceList] = results;
            if (sizes.status === 'fulfilled') setBinSizes(sizes.value);
            if (types.status === 'fulfilled') setBinTypes(types.value);
            if (groups.status === 'fulfilled') setBinGroups(groups.value);
            if (zoneList.status === 'fulfilled') setZones(zoneList.value);
            if (priorityList.status === 'fulfilled') setPriorities(priorityList.value);
            if (resourceList.status === 'fulfilled') setResources(resourceList.value);
            results
                .filter(r => r.status === 'rejected')
                .forEach(r => console.error('Failed to load bin reference data:', (r as PromiseRejectedResult).reason));
            setIsLoading(false);
        };

        load();
        return () => { cancelled = true; };
    }, [companyNo]);

    return useMemo(() => {
        const sizeMap = new Map(binSizes.map(s => [s.binSizeNo, s.description1]));
        const typeMap = new Map(binTypes.map(t => [t.binTypeNo, t.description1]));
        const grpMap = new Map(binGroups.map(g => [g.binGrpNo, g.description1]));
        const zoneMap = new Map(zones.map(z => [z.stcZoneID, z.description1]));
        const prioMap = new Map(priorities.map(p => [p.priorityNo, p.description1]));
        const resMap = new Map(resources.map(r => [r.resourceNo, r.description1]));
        const catMap = new Map(binCategoryOptions.map(o => [o.value, o.text]));

        const full = (id: number | string, desc?: string | null): string =>
            desc ? `${id} - ${desc}` : String(id);
        const short = (id: number | string, desc?: string | null): string =>
            desc || String(id);

        const build = (fmt: (id: number | string, desc?: string | null) => string): BinDescriptionResolvers => ({
            binSize: no => (no === null || no === undefined) ? '' : fmt(no, sizeMap.get(no)),
            binType: no => (no === null || no === undefined) ? '' : fmt(no, typeMap.get(no)),
            binGrp: no => (no === null || no === undefined) ? '' : fmt(no, grpMap.get(no)),
            binCat: no => (no === null || no === undefined) ? '' : fmt(no, catMap.get(no)),
            zone: id => !id ? '' : fmt(id, zoneMap.get(id)),
            priority: no => (no === null || no === undefined) ? '' : fmt(no, prioMap.get(no)),
            resource: no => (no === null || no === undefined) ? '' : fmt(no, resMap.get(no)),
        });

        return {
            isLoading,
            binSizes,
            binTypes,
            binGroups,
            zones,
            priorities,
            resources,
            describe: build(full),
            describeShort: build(short),
        };
    }, [isLoading, binSizes, binTypes, binGroups, zones, priorities, resources, binCategoryOptions]);
}
